import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Button, message, Modal, Spin } from 'antd';
import moment from 'moment';
import html2canvas from 'html2canvas';
import copy from 'copy-to-clipboard';
import intl from 'react-intl-universal';
import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import { fetchPaymentBillDetail } from '@/api/bill';
import { parseFen2Yuan } from '@/utils/number';
import { TrackEvents } from '@/config/trackEvent';
import { trackEvent } from '@/utils/track';
import { getTimeByUtcOffset, TenantSignStorage } from '@/utils/intl';
import { EXPORT_DATE_TIME_FORMAT } from '@/config';

import PaymentOrderContent from './PaymentOrderContent';
import { getPrintCss, getPrintHtml } from './PaymentOrderContent/generateDynamicHtml';
import { IPaymentOrderDetail } from './helper';
import styles from './index.module.scss';

type PaymentOrderModalProps = PropsWithChildren<{
  orderNo: string;
  open: boolean;
  onGenerateSuccess: () => void;
  onClose: () => void;
}>;

const win: any = window;
let iframe = null;

const PaymentOrderModal: React.FC<PaymentOrderModalProps> = ({
  orderNo,
  open,
  onGenerateSuccess,
  onClose,
}) => {
  const [detail, setDetail] = useState<IPaymentOrderDetail>();
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailLoadSuccess, setDetailLoadSuccess] = useState<boolean | null>(null);

  const [printLoading, setPrintLoading] = useState<boolean>(false);
  const [generateImgLoading, setGenerateImgLoading] = useState<boolean>(false);
  const [copyLoading, setCopyLoading] = useState<boolean>(false);

  const contentRef = useRef<any>();

  // 商户logo
  const logo = TenantSignStorage.getLocalConfig()?.icon2x || '';

  const handleImageToBase64 = async (imageUrl) => {
    let base64Url: any = null;
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');

      const blob = await response.blob();
      const reader = new FileReader();

      reader.onloadend = () => {
        base64Url = reader.result;
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error converting image to base64:', error);
    }
    return base64Url;
  };

  const initPaymentDetail = async () => {
    trackEvent(TrackEvents.YPMS_Cashier_Payment_Generate_click, {
      type: 'entry',
      name: intl.get('bill_pay_order_list_modal_title_vip_recharge_order'),
    });
    try {
      setDetailLoading(true);
      // 初始状态
      setDetailLoadSuccess(null);
      const res = await fetchPaymentBillDetail({ orderNo });
      if (res?.code === 0 && res.data) {
        setDetail({ ...res.data, qrcodeUrl: res.data?.sortUrl });
        setDetailLoadSuccess(true);
        if (onGenerateSuccess) {
          onGenerateSuccess();
        }
      } else {
        setDetailLoadSuccess(false);
      }
    } catch (error) {
      setDetailLoadSuccess(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const electronPrint = async (cssStr, htmlStr) => {
    win?.electron?.ipcRenderer?.sendPrintData({
      cssStr,
      htmlStr,
    });
  };

  const handlePrint = async () => {
    trackEvent(TrackEvents.YPMS_Cashier_Payment_Print_click, {
      type: 'action',
      name: intl.get('bill_pay_order_list_modal_title_vip_card_order_print'),
    });
    setPrintLoading(true);
    let imgSrc = '';
    // 拿到二维码URL
    try {
      const codeCanvas = await html2canvas(contentRef.current.getScanCodeRef(), {
        useCORS: true,
        scale: 4,
        allowTaint: false,
      });
      imgSrc = codeCanvas.toDataURL('png');
    } catch (error) {
      setPrintLoading(false);
    }
    // 拿到商户logo base64
    const logoBase64 = await handleImageToBase64(logo);

    const cssStr = getPrintCss();
    const htmlStr = getPrintHtml({ ...detail, qrcodeSrc: imgSrc || '', logoBase64 });
    if (win.electron) {
      electronPrint(cssStr, htmlStr);
    } else {
      try {
        if (iframe) {
          document.body.removeChild(iframe);
          iframe = null;
        }
        // 等待图片加载完成
        const image = new Image();
        image.src = imgSrc;
        image.onload = () => {
          iframe = document.createElement('iframe');
          document.body.appendChild(iframe);
          iframe.contentDocument.head.innerHTML = cssStr;
          iframe.contentDocument.body.innerHTML = htmlStr;
          iframe.contentWindow.print();
          setPrintLoading(false);
        };
      } catch (error) {
        //
      } finally {
        setPrintLoading(false);
      }
    }
  };

  const generateFileName = () => {
    const { customerName, totalAmt, commonCode, createTime } = detail || {};
    const fileName = `${customerName || ''}_${parseFen2Yuan(totalAmt)}${intl.get(
      'bill_pay_order_list_modal_file_name_content',
    )}_${commonCode}_${getTimeByUtcOffset(createTime, EXPORT_DATE_TIME_FORMAT)}.png`;
    return fileName;
  };

  const handleDownload = async () => {
    trackEvent(TrackEvents.YPMS_Cashier_Payment_Download_click, {
      type: 'action',
      name: intl.get('bill_pay_order_list_modal_pay_order_download'),
    });
    try {
      setGenerateImgLoading(true);
      const canvas = await html2canvas(contentRef.current.getChildrenRef(), {
        useCORS: true,
        scale: 4,
        allowTaint: false,
      });
      const imgSrc = canvas.toDataURL('png');
      if (imgSrc) {
        if (win?.electron) {
          win?.electron?.ipcRenderer?.download({
            downloadPath: imgSrc, // 下载链接
            fileName: generateFileName(), // 下载文件名，需要包含后缀名
          });
        } else {
          const a = document.createElement('a');
          a.href = imgSrc;
          a.download = generateFileName();
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }
    } catch (error) {
      //
    } finally {
      setGenerateImgLoading(false);
    }
  };

  const handleCopy = () => {
    trackEvent(TrackEvents.YPMS_Cashier_Payment_Copy_click, {
      type: 'action',
      name: intl.get('bill_pay_order_list_modal_copy_link'),
    });
    setCopyLoading(true);
    const isSuccess = copy(detail?.qrcodeUrl);
    if (isSuccess) {
      message.success(intl.get('bill_pay_order_list_modal_copy_link_success'));
      setCopyLoading(false);
    } else {
      setCopyLoading(false);
    }
  };

  const handleCancel = () => {
    if (iframe) {
      document.body.removeChild(iframe);
      iframe = null;
    }

    if (onClose) {
      onClose();
    }
  };

  const handleRetry = () => {
    initPaymentDetail();
  };

  useEffect(() => {
    if (orderNo && open) {
      initPaymentDetail();
      try {
        win?.electron?.ipcRenderer.on('print-result', ({ success }) => {
          setPrintLoading(false);
          if (success) {
            message.success(intl.get('bill_pay_order_list_modal_print_success'));
          } else {
            message.error(intl.get('bill_pay_order_list_modal_print_fail'));
          }
        });
      } catch (error) {
        setPrintLoading(false);
      }
    }
  }, [orderNo, open]);

  const renderContentByLoadingStatus = () => {
    if (detailLoading || detailLoadSuccess === null) {
      return (
        <>
          <div className={styles.iconContainer}>
            <SyncOutlined spin className={styles.tipIcon} />
            <div>{intl.get('bill_pay_order_list_modal_making')}</div>
          </div>
        </>
      );
    }
    if (detailLoadSuccess) {
      return (
        <>
          <div className={styles.tip}>{intl.get('bill_pay_order_list_modal_make_success')}</div>
          <div className={styles.contentWrapper}>
            <Spin spinning={printLoading || generateImgLoading}>
              <PaymentOrderContent ref={contentRef} detail={detail} />
            </Spin>
          </div>
        </>
      );
    }
    if (detailLoadSuccess === false) {
      return (
        <>
          <div className={styles.iconContainer}>
            <CloseOutlined className={styles.tipIcon} />
            <div>{intl.get('bill_pay_order_list_modal_make_fail')}</div>
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <>
      <Modal
        title={intl.get('bill_pay_order_list_payment_order')}
        destroyOnClose
        forceRender
        maskClosable={false}
        width={560}
        open={open}
        onCancel={handleCancel}
        wrapClassName={styles.modal}
        bodyStyle={{ padding: '24px 24px 20px' }}
        footer={
          <>
            {detailLoadSuccess ? (
              <>
                <div style={{ marginTop: 6, marginBottom: 16 }}>
                  复制链接仅支持微信发给客人，其他方式支持支付宝/微信扫码
                </div>
                <Button loading={printLoading} type="primary" onClick={handlePrint}>
                  {intl.get('app_print')}
                </Button>
                <Button loading={generateImgLoading} type="primary" onClick={handleDownload}>
                  {intl.get('app_download')}
                </Button>
                <Button loading={copyLoading} type="primary" onClick={handleCopy}>
                  {intl.get('app_copy_link')}
                </Button>
              </>
            ) : (
              <>
                <Button type="ghost" onClick={handleCancel}>
                  {intl.get('app_confirm_cancel_btn')}
                </Button>
                <Button type="primary" onClick={handleRetry}>
                  {intl.get('app_confirm_retry_btn')}
                </Button>
              </>
            )}
          </>
        }
      >
        <div className={styles.container}>{renderContentByLoadingStatus()}</div>
      </Modal>
    </>
  );
};

export default PaymentOrderModal;
